import { Box, Typography } from '@mui/material'
import { memo } from 'react'
import type { TEnumItem, TSong, TDraftReviewSong } from '@modules/types'
import { getEnumNameById } from '@modules/api-core'
import {
  getCurrencySymbol,
  getTextColor,
} from '@screens/requests/components/PublisherRequest/common/feeDisplayUtils'
import { getCounterOfferInfo, getDisplayPrice } from '../utils'
import { PriceDisplay } from './PriceDisplay'
import { PriceInput } from './PriceInput'
import { ActionButtons } from './ActionButtons'

const FIELD_WRAPPER_STYLES = {
  display: 'flex',
  flexDirection: 'column',
} as const

interface DualFeeQuotedPriceProps {
  song: TSong
  songHasStatus: boolean
  isLoading: boolean
  initialLoading: boolean
  editingState: { [key: string]: boolean }
  feeQuotedPriceValues: { [key: string]: string }
  draftReviewSong: TDraftReviewSong
  distributionMethod: number
  enums: TEnumItem[]
  onEdit: (songId: number, feeType?: 'print' | 'digital') => void
  onChange: (songId: number, value: string, feeType?: 'print' | 'digital') => void
  onDualSave: (songId: number) => void
  onCancel: (songId: number, feeType?: 'print' | 'digital') => void
  isBlocked?: boolean
  processingSongId?: number | null
  digitalCounterOfferFeeValidationError?: string
  printCounterOfferFeeValidationError?: string
  approvalDecision?: {
    isApproved: number
  } | null
}

export const DualFeeQuotedPrice = memo(
  ({
    song,
    songHasStatus,
    isLoading,
    initialLoading,
    editingState,
    feeQuotedPriceValues,
    draftReviewSong,
    distributionMethod,
    enums,
    onEdit,
    onChange,
    onDualSave,
    onCancel,
    isBlocked,
    processingSongId,
    digitalCounterOfferFeeValidationError,
    printCounterOfferFeeValidationError,
    approvalDecision,
  }: DualFeeQuotedPriceProps) => {
    const printKey = `${song.id}_print`
    const digitalKey = `${song.id}_digital`
    const isPrintEditing = editingState[printKey] || false
    const isDigitalEditing = editingState[digitalKey] || false
    const isEditing = isPrintEditing || isDigitalEditing
    const textColor = getTextColor(songHasStatus, isLoading)

    const printValue = feeQuotedPriceValues[printKey] || ''
    const digitalValue = feeQuotedPriceValues[digitalKey] || ''

    const printDisplayPrice = getDisplayPrice(
      printValue,
      draftReviewSong,
      song,
      distributionMethod,
      'print',
    )
    const digitalDisplayPrice = getDisplayPrice(
      digitalValue,
      draftReviewSong,
      song,
      distributionMethod,
      'digital',
    )

    const printCurrencyName = getEnumNameById(
      enums,
      'request_currency',
      song.printPublisherCurrency || 1,
    )
    const digitalCurrencyName = getEnumNameById(
      enums,
      'request_currency',
      song.digitalPublisherCurrency || 1,
    )
    const printCurrency = getCurrencySymbol(printCurrencyName)
    const digitalCurrency = getCurrencySymbol(digitalCurrencyName)
    const printCounterOfferInfo = getCounterOfferInfo(song, 'print')
    const digitalCounterOfferInfo = getCounterOfferInfo(song, 'digital')

    const handleEdit = () => {
      onEdit(song.id, 'print')
      onEdit(song.id, 'digital')
    }

    const handleSave = () => {
      onDualSave(song.id)
    }

    const handleCancel = () => {
      onCancel(song.id, 'print')
      onCancel(song.id, 'digital')
    }

    return (
      <Box display="flex" px={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={3.5}
          sx={{ color: textColor }}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            <Box sx={FIELD_WRAPPER_STYLES}>
              <Typography variant="caption" color={textColor}>
                Fee quoted price (Digital)
              </Typography>
              {isDigitalEditing ? (
                <PriceInput
                  value={digitalValue}
                  onChange={(songId, value) => onChange(songId, value, 'digital')}
                  songId={song.id}
                  initialLoading={initialLoading}
                  currency={digitalCurrency}
                  isBlocked={isBlocked}
                  validationError={digitalCounterOfferFeeValidationError}
                />
              ) : (
                <PriceDisplay
                  price={digitalDisplayPrice}
                  color={textColor}
                  currency={digitalCurrency}
                  initialLoading={initialLoading}
                  counterOfferInfo={digitalCounterOfferInfo}
                  enums={enums}
                  validationError={digitalCounterOfferFeeValidationError}
                />
              )}
            </Box>

            <Box sx={FIELD_WRAPPER_STYLES}>
              <Typography variant="caption" color={textColor}>
                Fee quoted price (Print)
              </Typography>
              {isPrintEditing ? (
                <PriceInput
                  value={printValue}
                  onChange={(songId, value) => onChange(songId, value, 'print')}
                  songId={song.id}
                  initialLoading={initialLoading}
                  currency={printCurrency}
                  isBlocked={isBlocked}
                  validationError={printCounterOfferFeeValidationError}
                />
              ) : (
                <PriceDisplay
                  price={printDisplayPrice}
                  color={textColor}
                  currency={printCurrency}
                  initialLoading={initialLoading}
                  counterOfferInfo={printCounterOfferInfo}
                  enums={enums}
                  validationError={printCounterOfferFeeValidationError}
                />
              )}
            </Box>
          </Box>
          <ActionButtons
            isEditing={isEditing}
            isLoading={isLoading}
            initialLoading={initialLoading}
            songId={song.id}
            onSave={handleSave}
            onCancel={handleCancel}
            onEdit={handleEdit}
            songHasStatus={songHasStatus}
            feeType="print"
            isBlocked={isBlocked}
            processingSongId={processingSongId}
            approvalDecision={approvalDecision}
          />
        </Box>
      </Box>
    )
  },
)
